import { getApiBaseUrl, getAuthConfig, ProviderConfig } from './authConfig';
import { SVVTOKENS_KEY, LAST_LOGIN_KEY, AUTH_PROVIDER_KEY } from './AuthenticationUtils';

export type AuthProvider = 'employee' | 'idporten';

const OAUTH_STATE_KEY = 'oauth_state';
const OAUTH_CALLBACK_KEY = 'oauth_callback_complete';
const OAUTH_RETURN_HASH_KEY = 'oauth_return_hash';

interface StoredOAuthState {
    state: string;
    nonce: string;
    provider: AuthProvider;
    codeVerifier: string;
    issuer: string;
    apiBaseUrl: string;
    clientId: string;
}

export function generateRandomString(length: number): string {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return base64UrlEncode(array.buffer);
}

function base64UrlEncode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(digest);
}

export function storeOAuthState(
    state: string,
    nonce: string,
    provider: AuthProvider,
    codeVerifier: string,
    issuer: string,
    apiBaseUrl: string,
    clientId: string
): void {
    const oauthState: StoredOAuthState = {
        state,
        nonce,
        provider,
        codeVerifier,
        issuer,
        apiBaseUrl,
        clientId,
    };
    // Use localStorage so popup window can access the state
    localStorage.setItem(OAUTH_STATE_KEY, JSON.stringify(oauthState));
}

export function getStoredOAuthState(): StoredOAuthState | null {
    const stored = localStorage.getItem(OAUTH_STATE_KEY);
    if (!stored) return null;
    try {
        return JSON.parse(stored);
    } catch {
        return null;
    }
}

export function clearOAuthState(): void {
    localStorage.removeItem(OAUTH_STATE_KEY);
}

function getProviderConfig(provider: AuthProvider): ProviderConfig | null {
    const authConfig = getAuthConfig();
    if (!authConfig) return null;
    return provider === 'employee' ? authConfig.employee : authConfig.idporten;
}

function getRedirectUri(): string {
    return window.location.origin + '/oauth-callback.html';
}

export async function startOAuth2Flow(provider: AuthProvider): Promise<void> {
    const providerConfig = getProviderConfig(provider);
    const apiBaseUrl = getApiBaseUrl();

    if (!providerConfig || !apiBaseUrl) {
        console.error(
            'Auth config not available - providerConfig:',
            providerConfig,
            'apiBaseUrl:',
            apiBaseUrl
        );
        return;
    }

    const state = generateRandomString(32);
    const nonce = generateRandomString(32);
    const codeVerifier = generateRandomString(64);
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    storeOAuthState(
        state,
        nonce,
        provider,
        codeVerifier,
        providerConfig.issuer,
        apiBaseUrl,
        providerConfig.clientId
    );

    const params = new URLSearchParams({
        response_type: 'code',
        client_id: providerConfig.clientId,
        redirect_uri: getRedirectUri(),
        scope: providerConfig.scope,
        state: state,
        nonce: nonce,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        acr_values: providerConfig.acrValues,
    });

    const authorizeUrl = `${providerConfig.issuer}/authorize?${params.toString()}`;

    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const popup = window.open(
        authorizeUrl,
        'oauth_popup',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
    );

    sessionStorage.setItem(AUTH_PROVIDER_KEY, provider);

    // If the popup was blocked (e.g. on iPad/iPhone), fall back to redirect flow
    if (!popup) {
        localStorage.setItem(OAUTH_RETURN_HASH_KEY, window.location.hash);
        window.location.href = authorizeUrl;
    }
}

export function listenForOAuthCallback(onSuccess: () => void): () => void {
    const handler = (event: StorageEvent) => {
        if (event.key === OAUTH_CALLBACK_KEY && event.newValue === 'true') {
            localStorage.removeItem(OAUTH_CALLBACK_KEY);
            onSuccess();
        }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
}

export async function handleOAuth2Callback(): Promise<{
    access_token: string;
    id_token?: string;
} | null> {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    const returnedState = urlParams.get('state');

    if (error) {
        console.error('OAuth error:', error, urlParams.get('error_description'));
        clearOAuthState();
        cleanUrlParams();
        return null;
    }

    if (!code) {
        return null;
    }

    const storedState = getStoredOAuthState();
    if (!storedState) {
        console.error('No stored OAuth state found in sessionStorage');
        cleanUrlParams();
        return null;
    }

    if (returnedState !== storedState.state) {
        console.error(
            'OAuth state mismatch - returned:',
            returnedState,
            'stored:',
            storedState.state
        );
        clearOAuthState();
        cleanUrlParams();
        return null;
    }

    try {
        // Token exchange directly with OAuth provider
        // For OpenAM/ForgeRock, the token endpoint is at {issuer}/access_token
        const tokenUrl = `${storedState.issuer}/access_token`;

        // OAuth 2.0 token exchange requires form-encoded body
        const requestBody = new URLSearchParams({
            grant_type: 'authorization_code',
            code: code,
            redirect_uri: getRedirectUri(),
            code_verifier: storedState.codeVerifier,
            client_id: storedState.clientId,
        });

        const response = await fetch(tokenUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            mode: 'cors',
            body: requestBody.toString(),
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(
                'handleOAuth2Callback: Token exchange failed with status',
                response.status
            );
            console.error('handleOAuth2Callback: Error response body:', errorText);
            try {
                const errorJson = JSON.parse(errorText);
                console.error('handleOAuth2Callback: Parsed error:', errorJson);
            } catch {
                // Not JSON, already logged as text
            }
            clearOAuthState();
            cleanUrlParams();
            return null;
        }

        const tokens = await response.json();

        clearOAuthState();

        // Store tokens in sessionStorage (for redirect flow in main window)
        if (tokens?.access_token) {
            sessionStorage.setItem(SVVTOKENS_KEY, JSON.stringify(tokens));
            sessionStorage.setItem(LAST_LOGIN_KEY, new Date().toString());
        } else {
            console.error('handleOAuth2Callback: No access_token in response!');
            console.error('handleOAuth2Callback: Full response:', tokens);
        }

        // Note: Window closing is now handled by handleOAuthPopupAsync() to ensure
        // proper timing and storage event propagation
        cleanUrlParams();
        return tokens;
    } catch (err) {
        console.error('Error exchanging code for token:', err);
        clearOAuthState();
        if (!window.opener) {
            cleanUrlParams();
        }
        return null;
    }
}

export function isOAuthPopup(): boolean {
    return hasOAuthCallback() && window.opener !== null;
}

function cleanUrlParams(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    url.searchParams.delete('error');
    url.searchParams.delete('error_description');
    window.history.replaceState({}, document.title, url.pathname + url.hash);
}

export function hasOAuthCallback(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('code') || urlParams.has('error');
}

/**
 * Handle OAuth popup early in the app lifecycle.
 * This function should be called at the very beginning of main.tsx.
 * If this is an OAuth popup with a callback, it will:
 * 1. Show a loading message
 * 2. Exchange the code for tokens
 * 3. Store tokens and signal the parent window
 * 4. Close the popup
 *
 * @returns true if this is an OAuth popup being handled (app should not render),
 *          false if this is a normal page load (app should render normally)
 */
export function handleOAuthPopup(): boolean {
    // Check if this is an OAuth callback in a popup
    if (!hasOAuthCallback() || !window.opener) {
        return false;
    }

    // Show a loading message while we handle the callback
    const container = document.getElementById('root');
    if (container) {
        container.innerHTML = `
            <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: sans-serif;">
                <p>Logger inn...</p>
                <p style="font-size: 12px; color: #666;">Dette vinduet lukkes automatisk.</p>
            </div>
        `;
    }

    // Handle the OAuth callback asynchronously
    handleOAuthPopupAsync();

    return true;
}

async function handleOAuthPopupAsync(): Promise<void> {
    try {
        // Get the stored state to find the API base URL
        const storedState = getStoredOAuthState();

        if (!storedState) {
            throw new Error(
                'Ingen OAuth state funnet i localStorage. Dette kan skje hvis du har lukket og gjenåpnet popup-vinduet.'
            );
        }

        if (!storedState.apiBaseUrl) {
            throw new Error('API base URL mangler i OAuth state.');
        }

        // We need to load the auth config first since it's needed for the token exchange
        const { loadAuthConfig } = await import('./authConfig');
        const config = await loadAuthConfig(storedState.apiBaseUrl);

        if (!config) {
            throw new Error(
                `Kunne ikke laste auth config fra ${storedState.apiBaseUrl}/api/v1/auth-config`
            );
        }

        const tokens = await handleOAuth2Callback();

        if (tokens?.access_token) {
            await new Promise(resolve => setTimeout(resolve, 100));
            tryCloseWindow();
        } else {
            console.error('Token exchange failed - no access_token received');

            // Show detailed error message
            const container = document.getElementById('root');
            if (container) {
                const errorDetails = tokens
                    ? JSON.stringify(tokens, null, 2)
                    : 'No response';
                container.innerHTML = `
                    <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: sans-serif; padding: 20px;">
                        <h3 style="color: red;">Innlogging feilet</h3>
                        <p style="font-size: 14px; margin: 20px 0;">Ingen access_token mottatt fra serveren.</p>
                        <details style="max-width: 600px; width: 100%;">
                            <summary style="cursor: pointer; font-size: 12px; color: #666;">Vis detaljer</summary>
                            <pre style="background: #f5f5f5; padding: 10px; overflow: auto; font-size: 11px; margin-top: 10px;">
Response: ${errorDetails}

Se console for mer informasjon.
                            </pre>
                        </details>
                        <p style="font-size: 12px; color: #666; margin-top: 20px;">
                            Dette vinduet lukkes IKKE automatisk - lukk det manuelt etter du har lest feilen.
                        </p>
                    </div>
                `;
            }
        }
    } catch (err) {
        console.error('OAuth popup: Error during callback handling', err);
        const error = err instanceof Error ? err : new Error(String(err));

        const container = document.getElementById('root');
        if (container) {
            container.innerHTML = `
                <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: sans-serif; padding: 20px;">
                    <h3 style="color: red;">En feil oppstod</h3>
                    <p style="font-size: 14px; margin: 20px 0;">${error.message || 'Ukjent feil'}</p>
                    <details style="max-width: 600px; width: 100%;">
                        <summary style="cursor: pointer; font-size: 12px; color: #666;">Vis detaljer</summary>
                        <pre style="background: #f5f5f5; padding: 10px; overflow: auto; font-size: 11px; margin-top: 10px;">
${error.stack || error.toString()}

Se console for mer informasjon.
                        </pre>
                    </details>
                    <p style="font-size: 12px; color: #666; margin-top: 20px;">
                        Dette vinduet lukkes IKKE automatisk - lukk det manuelt etter du har lest feilen.
                    </p>
                </div>
            `;
        }
    }
}

function tryCloseWindow(): void {
    // Try to close immediately
    window.close();

    // If window didn't close, try again after a short delay
    setTimeout(() => {
        window.close();
    }, 100);

    // Try one more time after another delay
    setTimeout(() => {
        window.close();
    }, 500);
}
